import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, NavLink, useLocation, useHistory, useParams, Redirect, Switch } from 'react-router-dom';

import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import Hidden from '@material-ui/core/Hidden';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from '@material-ui/core/Button';

import AccountIcon from '@material-ui/icons/AccountCircle';
import MembershipIcon from '@material-ui/icons/VerifiedUser';
import PaymentIcon from '@material-ui/icons/Payment';
import PasswordIcon from '@material-ui/icons/Https';
import HistoryIcon from '@material-ui/icons/History'
import LeftIcon from '@material-ui/icons/ArrowBackIos';

import Snapshot from 'app/components/user/Snapshot';
import Profile from 'app/components/user/Profile';
import Membership from 'app/components/user/Membership';
import Accounting from 'app/components/user/Accounting';
import Security from 'app/components/user/Security';
import History from 'app/components/user/History';

import useApi from 'app/context/useApi';

import { containerFlex, containerFlexFixedSmall, containerFlexGrow } from 'app/styles/new-layout'

const useStyles = makeStyles(theme => ({

    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3)
    },
    nav: {
        ...containerFlexFixedSmall(theme),
    },
    content: {
        ...containerFlexGrow(theme),
        paddingLeft: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            paddingLeft: '0px',
        }
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius
    },
    bottomNavigation: {
        left: 0,
        width: '100%',
        bottom: 0,
        zIndex: 100,
        position: 'fixed',
        boxShadow: '0px 5px 10px #111111'
    },
    bottomNavigationAction: {
        minWidth: 60
    },
    bottomNavigationSpacer: theme.mixins.toolbar
}));

export default function EditUser() {

    let location = useLocation();
    let history = useHistory();
    let params = useParams();
    const api = useApi();

    const userId = params.id;

    const [ user, setUser ] = useState(null);
    const [ association, setAssociation ] = useState(null);
    const [ customValues, setCustomValues ] = useState(null);
    const [ userLoading, setUserLoading ] = useState(true);

    const [ levels, setLevels ] = useState([]);
    const [ levelsLoading, setLevelsLoading ] = useState(false)

    const classes = useStyles();

    const pathname = location.pathname;
    const urlPrefix = '/admin/user/' + userId;

    // Get Functions ------------------------------------------

    const getUser = () => {
        setUserLoading(true)

        api.get(urlPrefix)
            .then(res => {
                setUser(res.user);
                setAssociation(res.association);
                setCustomValues(res.association.values);
            }).finally(setUserLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => {
                setLevels(res.groups);
            }).finally(setLevelsLoading(false));
    }

    useEffect(() => {
        if (userId > 0) getUser();
        else setUserLoading(false);

        getLevels();
    }, []);

    // Navigation Functions ------------------------------------------

    const handleBottomNavChange = (event, value) => {
        history.push(value);
    }

    const isSelected = tab => (tab === pathname);

    return (
        <div className={classes.container}>
            { (userId > 0) &&
                <div className={classes.nav}>
                    <Snapshot urlPrefix={urlPrefix} user={user} getUser={getUser}/>
                    <div className={classes.marginTop}/>
                    <Hidden smDown>
                        <Divider/>
                        <MenuList>
                            <MenuItem
                                to={'/admin/users/' + userId}
                                className={classes.menuItem}
                                selected={isSelected('/admin/users/' + userId)}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <AccountIcon />
                                </ListItemIcon>
                                <ListItemText primary="Profile" />
                            </MenuItem>
                            <MenuItem
                                to={'/admin/users/' + userId + '/membership'}
                                className={classes.menuItem}
                                selected={isSelected('/admin/users/' + userId + '/membership')}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <MembershipIcon />
                                </ListItemIcon>
                                <ListItemText primary="Membership" />
                            </MenuItem>
                            <MenuItem
                                to={'/admin/users/' + userId + '/payment'}
                                className={classes.menuItem}
                                selected={isSelected('/admin/users/' + userId + '/payment')}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <PaymentIcon />
                                </ListItemIcon>
                                <ListItemText primary="Payment" />
                            </MenuItem>
                            <MenuItem
                                to={'/admin/users/' + userId + '/security'}
                                className={classes.menuItem}
                                selected={isSelected('/admin/users/' + userId + '/security')}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <PasswordIcon />
                                </ListItemIcon>
                                <ListItemText primary="Security" />
                            </MenuItem>
                            <MenuItem
                                to={'/admin/users/' + userId + '/history'}
                                className={classes.menuItem}
                                selected={isSelected('/admin/users/' + userId + '/history')}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <HistoryIcon />
                                </ListItemIcon>
                                <ListItemText primary="History" />
                            </MenuItem>
                        </MenuList>
                        <Divider/>
                        <MenuList>
                            <MenuItem
                                to={'/admin/users'}
                                className={classes.menuItem}
                                component={NavLink}
                                >
                                <ListItemIcon>
                                    <LeftIcon />
                                </ListItemIcon>
                                <ListItemText primary="Back to Users" />
                            </MenuItem>
                        </MenuList>
                    </Hidden>
                </div>
            }
            <div className={classes.content}>
                {(association?.deleted_at !== null && !userLoading) &&
                    <Alert
                        className={classes.marginBottom}
                        severity="warning"
                        action={
                            <Button color="inherit" size="small">
                                Restore User
                            </Button>
                        }
                        >
                        <AlertTitle>User Deleted</AlertTitle>
                        This user has been removed from your organization.
                    </Alert>
                }
                <Switch>
                    <Route path={'/admin/users/' + userId} exact>
                        <Profile user={user} urlPrefix={urlPrefix} getUser={getUser} admin deleted={association?.deleted_at} customValues={customValues}/>
                    </Route>
                    <Route path={'/admin/users/' + userId + '/membership'} exact>
                        <Membership
                            levels={levels}
                            levelsLoading={levelsLoading}
                            urlPrefix={urlPrefix}
                            deleted={association?.deleted_at}
                        />
                    </Route>
                    <Route path={'/admin/users/' + userId + '/payment'} exact>
                        <Accounting userId={userId} user={user} urlPrefix={urlPrefix} deleted={association?.deleted_at}/>
                    </Route>
                    <Route path={'/admin/users/' + userId + '/security'} exact>
                        <Security userId={userId} user={user} urlPrefix={urlPrefix} admin deleted={association?.deleted_at}/>
                    </Route>
                    <Route path={'/admin/users/' + userId + '/history'} exact>
                        <History userId={userId} deleted={association?.deleted_at}/>
                    </Route>
                    <Redirect from={'/admin/users/' + userId + '/*'} to={'/admin/users/' + userId}/>
                </Switch>
            </div>
            <Hidden mdUp>
                <div className={classes.bottomNavigationSpacer}/>
                <BottomNavigation
                    onChange={handleBottomNavChange}
                    value={pathname}
                    className={classes.bottomNavigation}
                    >
                    <BottomNavigationAction
                        label="Back"
                        value='/admin/users'
                        icon={<LeftIcon/>}
                        classes={{ root: classes.bottomNavigationAction }}
                    />
                    <BottomNavigationAction
                        label="Profile"
                        value={'/admin/users/' + userId}
                        icon={<AccountIcon/>}
                        classes={{ root: classes.bottomNavigationAction }}
                    />
                    <BottomNavigationAction
                        label="Membership"
                        value={'/admin/users/' + userId + '/membership'}
                        icon={<MembershipIcon/>}
                        classes={{ root: classes.bottomNavigationAction }}
                    />
                    <BottomNavigationAction
                        label="Payment"
                        value={'/admin/users/' + userId + '/payment'}
                        icon={<PaymentIcon/>}
                        classes={{ root: classes.bottomNavigationAction }}
                    />
                    <BottomNavigationAction
                        label="Security"
                        value={'/admin/users/' + userId + '/security'}
                        icon={<PasswordIcon/>}
                        classes={{ root: classes.bottomNavigationAction }}
                    />
                </BottomNavigation>
            </Hidden>
        </div>
    );
}
